import { createStyles, makeStyles, Theme } from '@material-ui/core';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import MuiAnalyticsIcon from '@material-ui/icons/InsertChart';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import React from 'react';

import { ReactComponent as PublishSvg } from '../../../../assets/publish.svg';
import { ReactComponent as ShareSvg } from '../../../../assets/share.svg';
import { ReactComponent as UnpublishSvg } from '../../../../assets/unpublish.svg';
import {
  FullAnswerFragment,
  QuestionDetailsFragment,
  ReactionType,
  useDeleteQuestionMutation,
  usePublishFactMutation,
  useReactToQuestionMutation,
} from '../../../../graphql/gen/types';
import { usePublishStatusFilter } from '../../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../../models/filters/useSelectedLabelIds';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexIconButtonHref } from '../../../shared/primitives/PlexIconButtonHref';
import { useDialogState } from '../../dialog/useDialogState';
import { SharePopover } from '../../feed/SharePopover';
import { useSharePopover } from '../../feed/useSharePopover';
import {
  canDeleteQuestion,
  canEditQuestion,
  canPublishFact,
  canUnpublishFact,
  canViewQuestionAnalytics,
} from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';

type Props = {
  plexQuestionData: QuestionDetailsFragment;
};

export function PlexQuestionActions(props: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();
  const [shareAnchorEl, handleClickShare, handleCloseShare] = useSharePopover();
  const { plexQuestionData } = props;
  const { id, source } = plexQuestionData;
  // TODO: handle error
  const [deleteQuestion, { loading: deleteLoading }] = useDeleteQuestionMutation();
  const [publishFact, { loading: publishFactLoading }] = usePublishFactMutation();

  // TODO: handle error
  const [reactToQuestionMutation, { loading: likeLoading }] = useReactToQuestionMutation();
  const confirmAction = useConfirmAction();

  const classes = useStyles();

  const filterPublishStatus = usePublishStatusFilter();
  const labelIds = useSelectedLabelIds();
  const { status, currentUserReactions, reactionCounts, plex } = plexQuestionData;
  const { id: plexId } = plex;
  const plexRelation = plexQuestionData.plex.currentUserRelation.state;
  const questionRelation = plexQuestionData.currentUserRelation;

  const currentUserLiked = currentUserReactions.includes(ReactionType.Like);
  const likesCount = reactionCounts.find((reactionCount) => reactionCount.type === ReactionType.Like)?.count || 0;

  const acceptedAnswer: FullAnswerFragment | null =
    plexQuestionData.acceptedAnswer != null && !plexQuestionData.acceptedAnswer.deleted
      ? plexQuestionData.acceptedAnswer
      : null;

  const handleLike = (): void => {
    reactToQuestionMutation({
      variables: { reaction: { type: ReactionType.Like, questionId: plexQuestionData.id, checked: !currentUserLiked } },
    }).then(() => {
      if (currentUserLiked) {
        logAnalytics({ name: 'faq liked', details: { plexId, itemId: id } });
      }
    });
  };

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this question?',
      confirmButton: {
        onClick: (): void => {
          deleteQuestion({
            variables: { questionId: id, filterPublishStatus, labelIds },
          }).then(() => logAnalytics({ name: 'faq deleted', details: { plexId, itemId: id } }));
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const handleOpenAnalytics = (): void => {
    setDialogState({ type: 'fact_analytics_from_fact', factId: id });
  };

  const handlePublish = (): void => {
    if (plexQuestionData != null) {
      publishFact({
        variables: {
          publishQuestionInput: {
            questionId: plexQuestionData.id,
            checked: true,
          },
        },
      }).then(() => logAnalytics({ name: 'faq published', details: { plexId, itemId: id } }));
    }
  };

  const handleUnpublish = (): void => {
    if (plexQuestionData != null) {
      publishFact({
        variables: {
          publishQuestionInput: {
            questionId: plexQuestionData.id,
            checked: false,
          },
        },
      }).then(() => logAnalytics({ name: 'faq unpublished', details: { plexId, itemId: id } }));
    }
  };
  const disabled = deleteLoading || publishFactLoading || likeLoading;

  return (
    <div className={classes.root}>
      {canEditQuestion({ plexRelation, questionRelation }) && source != null && (
        <PlexIconButtonHref
          className={classes.actionButton}
          MuiIcon={DescriptionIcon}
          href={source.url}
          color={ButtonColor.Purple}
          tooltip={'View Source - Google Document'}
        />
      )}
      <PlexIconButton
        className={classes.actionButton}
        disabled={disabled}
        onClick={handleClickShare}
        SvgIcon={ShareSvg}
        color={ButtonColor.Purple}
        tooltip={'Share'}
      />
      <SharePopover itemId={id} itemType="question" anchorEl={shareAnchorEl} handleClose={handleCloseShare} />
      {!!acceptedAnswer && canPublishFact({ plexRelation, status }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handlePublish}
          SvgIcon={PublishSvg}
          color={ButtonColor.Purple}
          tooltip={'Publish'}
        />
      )}
      {canUnpublishFact({ plexRelation, status }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleUnpublish}
          SvgIcon={UnpublishSvg}
          color={ButtonColor.Purple}
          tooltip={'Un-publish'}
        />
      )}
      <PlexIconButton
        className={classes.actionButton}
        disabled={disabled}
        onClick={handleLike}
        MuiIcon={ThumbUpIcon}
        color={ButtonColor.Purple}
        tooltip={currentUserLiked ? `Unlike question (${likesCount})` : `Like question (${likesCount})`}
      />
      {canViewQuestionAnalytics(plexRelation) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleOpenAnalytics}
          MuiIcon={MuiAnalyticsIcon}
          color={ButtonColor.Purple}
          tooltip={'Analytics'}
        />
      )}
      {canDeleteQuestion({ plexRelation, questionRelation }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleDelete}
          MuiIcon={MuiDeleteIcon}
          color={ButtonColor.TransparentRed}
          tooltip={'Delete'}
        />
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      position: 'absolute',
      flexDirection: 'column',
      top: 0,
      right: 0,
    },
    actionButton: {
      marginTop: spacings.normal,
      marginRight: spacings.xlarge,
    },
  })
);
